import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { supabase } from '@/lib/supabase';
import { gcsUploadFile } from '@/lib/gcsStorage';
import { useAuth } from '@/contexts/AuthContext';
import { sendNotification } from '@/lib/notifications';
import { AppShell3 } from '@/components/app-shell-3';
import { SharedNavbar } from '@/components/SharedNavbar';
import { GlobalToolsExpandingPanel } from '@/components/GlobalToolsExpandingPanel';
import { ToolHeader } from '@/components/ToolHeader';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { PageFadeIn } from '@/components/PageFadeIn';
import { PageTransition } from '@/components/PageTransition';
import { formatFileSize, getFileIcon, getFileIconColor, getFileTypeLabel } from '@/components/DocumentUpload';
import { DocumentPostViewer } from '@/components/DocumentPostViewer';
import {
  MessageSquare,
  Plus,
  Pin,
  Lock,
  MoreVertical,
  Edit,
  Trash2,
  ArrowLeft,
  Send,
  User,
  Link2,
  Image as ImageIcon,
  FileText,
  FileUp,
  Upload,
  Download,
  X,
  ExternalLink,
  Bold,
  Italic,
  List,
  ListOrdered,
  Code,
  Quote,
  Users,
  Building2,
  Layers,
  Filter,
  GraduationCap,
  CalendarCheck,
  Check,
  ChevronsUpDown,
  Video,
  Camera,
  Pause,
  Play,
  Square,
  RotateCcw,
  CircleDot,
  CheckCircle2,
  Search,
  ChevronRight,
  ChevronLeft,
  BarChart3,
  Boxes,
  GitBranch,
  Grid3X3,
  ClipboardCheck,
  CheckSquare
} from 'lucide-react';
import { TOOL_REGISTRY as toolConfigs, GLOBAL_TOOLS } from '@/config/tools';
// eslint-disable-next-line no-unused-vars -- motion is used via motion.div in JSX
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { RichTextEditor } from '@/components/RichTextEditor';
import { TipTapContent } from '@/components/TipTapContent';
import { usePostsData } from '@/hooks/usePostsData';

export default function MessageBoardPage({
  stackId,
  stackName,
  stackImage,
  stackProjects,
  isStackView = false,
  isGlobalView = false
}) {
  const { projectId, postId } = useParams();
  const navigate = useNavigate();
  const [globalToolsOpen, setGlobalToolsOpen] = useState(false);
  const handleGlobalToolsToggle = useCallback(() => setGlobalToolsOpen(prev => !prev), []);
  const { userProfile, company } = useAuth();

  // Set page title
  usePageTitle(isGlobalView ? 'Posts' : isStackView ? `${stackName} Posts` : 'Posts');

  // Determine view type
  const isSingleSpaceView = !isGlobalView && !isStackView && !!projectId;

  // Determine which project IDs to use
  const projectIds = React.useMemo(() => {
    if (isGlobalView) return []; // Global view shows all company posts
    return isStackView ? stackProjects?.map(p => p.id) : [projectId];
  }, [isStackView, stackProjects, projectId, isGlobalView]);

  // Local UI state
  const [project, setProject] = useState(null);
  const [space, setSpace] = useState(null);
  const [spaceTools, setSpaceTools] = useState([]);
  const [allAvailableSpaces, setAllAvailableSpaces] = useState([]); // All spaces user has access to
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPost, setSelectedPost] = useState(null);

  // Use optimized hook for posts data (replaces inline fetchPosts)
  const {
    posts,
    loading,
    refetch: refetchPosts,
    fetchPostDetail,
    updatePostLocally,
    removePostLocally
  } = usePostsData(company?.id, userProfile?.id, {
    projectIds: projectIds,
    search: searchQuery,
    isGlobalView,
    page: 1,
    limit: 50, // Load more posts since no pagination UI yet
    enabled: !!(company?.id && (isGlobalView || (projectIds && projectIds.length > 0 && projectIds[0])))
  });
  const [userTracking, setUserTracking] = useState(null); // Current user's tracking status for selected post
  const [_trackingStats, setTrackingStats] = useState(null); // Tracking statistics for selected post
  const [allTracking, setAllTracking] = useState([]); // All tracking records (for authors only)
  const [comments, setComments] = useState([]);
  const [isQuizModalOpen, setIsQuizModalOpen] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(null);
  const [_currentVideoTime, setCurrentVideoTime] = useState(0);
  const [activeWordIndex, setActiveWordIndex] = useState(-1);
  const [_videoBuffered, setVideoBuffered] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoHeight, setVideoHeight] = useState(500);
  const [isTranscriptVisible, setIsTranscriptVisible] = useState(true);
  const videoRef = useRef(null);
  const activeWordRef = useRef(null);
  const transcriptScrollRef = useRef(null);

  // Auto-scroll active word within transcript container only
  useEffect(() => {
    if (activeWordRef.current && transcriptScrollRef.current && activeWordIndex !== -1) {
      const wordElement = activeWordRef.current;
      const scrollContainer = transcriptScrollRef.current;

      // Get positions
      const containerRect = scrollContainer.getBoundingClientRect();
      const wordRect = wordElement.getBoundingClientRect();

      // Check if word is outside visible area
      const isAboveView = wordRect.top < containerRect.top;
      const isBelowView = wordRect.bottom > containerRect.bottom;

      if (isAboveView || isBelowView) {
        // Scroll to center the word in the container
        const scrollOffset = wordElement.offsetTop - scrollContainer.offsetTop - (scrollContainer.clientHeight / 2) + (wordElement.clientHeight / 2);
        scrollContainer.scrollTo({
          top: scrollOffset,
          behavior: 'smooth'
        });
      }
    }
  }, [activeWordIndex]);

  // Dynamically update transcript height to match video height
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const updateVideoHeight = () => {
      const height = videoElement.offsetHeight;
      if (height > 0) {
        setVideoHeight(height);
      }
    };

    // Update height on video load and when dimensions are available
    const handleLoadedMetadata = () => {
      // Use requestAnimationFrame to ensure the video has rendered
      requestAnimationFrame(() => {
        updateVideoHeight();
      });
    };

    const handleLoadedData = () => {
      requestAnimationFrame(() => {
        updateVideoHeight();
      });
    };

    videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
    videoElement.addEventListener('loadeddata', handleLoadedData);
    window.addEventListener('resize', updateVideoHeight);

    // Use ResizeObserver for better tracking
    const resizeObserver = new ResizeObserver(() => {
      updateVideoHeight();
    });
    resizeObserver.observe(videoElement);

    // Try to set initial height with a small delay
    const timer = setTimeout(() => {
      updateVideoHeight();
    }, 100);

    return () => {
      videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      videoElement.removeEventListener('loadeddata', handleLoadedData);
      window.removeEventListener('resize', updateVideoHeight);
      resizeObserver.disconnect();
      clearTimeout(timer);
    };
  }, [selectedPost]);

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [postType, setPostType] = useState('text');
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [videoPreview, setVideoPreview] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [recordingState, setRecordingState] = useState('idle'); // 'idle', 'preview', 'recording', 'paused', 'reviewing'
  const [mediaStream, setMediaStream] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordingTimer, setRecordingTimer] = useState(0);
  const [timerInterval, setTimerInterval] = useState(null);
  const dialogScrollRef = useRef(null);
  const [selectedSpaceFilter, setSelectedSpaceFilter] = useState('all');
  const [selectedSpaceFilters, setSelectedSpaceFilters] = useState([]); // For global view multi-select
  const [showTrackingDetails, setShowTrackingDetails] = useState(false);
  const [trackingFilters, setTrackingFilters] = useState({
    statuses: [] // 'needs_reading', 'needs_understanding', 'completed_reading', 'completed_understanding'
  });
  const [tempTrackingFilters, setTempTrackingFilters] = useState({
    statuses: []
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [visibilityType, setVisibilityType] = useState(
    isGlobalView ? 'company' : (isStackView ? 'stack' : 'space')
  ); // 'company', 'space', or 'stack'
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    linkUrl: '',
    imageUrl: '',
    imageCaption: '',
    videoUrl: '',
    selectedProjectId:
      isStackView && stackProjects?.[0]?.id ? stackProjects[0].id : projectId,
    selectedProjectIds: isStackView ? stackProjects?.map(p => p.id) || [] : (projectId ? [projectId] : []),
    requiresReading: false,
    requiresUnderstanding: false
  });

  // Global tools for company-wide navigation (from central config)
  const globalTools = React.useMemo(() => {
    if (!isGlobalView) return [];
    return GLOBAL_TOOLS;
  }, [isGlobalView]);

  // Memoized handler for rich text editor to prevent unnecessary re-renders
  const handleContentChange = useCallback((html) => {
    setFormData(prev => ({ ...prev, content: html }));
  }, []);

  // Note: Search debouncing is handled by usePostsData hook

  // Fetch project details
  useEffect(() => {
    const fetchProject = async () => {
      if (isGlobalView) {
        // For global view, we don't need project details
        return;
      }

      if (isStackView) {
        // For stack view, set up standard tools
        const standardTools = [
          {
            id: 'matrix',
            tool_type: 'matrix',
            name: toolConfigs.matrix.name,
            icon: toolConfigs.matrix.icon,
            color: toolConfigs.matrix.color
          },
          {
            id: 'team',
            tool_type: 'team',
            name: toolConfigs.team.name,
            icon: toolConfigs.team.icon,
            color: toolConfigs.team.color
          },
          {
            id: 'compliance',
            tool_type: 'compliance',
            name: toolConfigs.compliance.name,
            icon: toolConfigs.compliance.icon,
            color: toolConfigs.compliance.color
          },
          {
            id: 'bookings',
            tool_type: 'bookings',
            name: toolConfigs.bookings.name,
            icon: toolConfigs.bookings.icon,
            color: toolConfigs.bookings.color
          },
          {
            id: 'calendar',
            tool_type: 'calendar',
            name: toolConfigs.calendar.name,
            icon: toolConfigs.calendar.icon,
            color: toolConfigs.calendar.color
          },
          {
            id: 'checklists',
            tool_type: 'checklists',
            name: toolConfigs.checklists.name,
            icon: toolConfigs.checklists.icon,
            color: toolConfigs.checklists.color
          },
          {
            id: 'message_board',
            tool_type: 'message_board',
            name: toolConfigs.message_board.name,
            icon: toolConfigs.message_board.icon,
            color: toolConfigs.message_board.color
          }
        ];
        setSpaceTools(standardTools);
        return;
      }

      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('id', projectId)
        .single();

      if (error) {
        console.error('Error fetching project:', error);
        return;
      }

      setProject(data);
      setSpace(data);

      // Process tools from the space
      if (data && data.tools) {
        const toolsObj =
          typeof data.tools === 'string' ? JSON.parse(data.tools) : data.tools;
        const tools = Object.entries(toolsObj)
          .filter(([key, enabled]) => enabled === true)
          .map(([toolKey]) => ({
            id: toolKey,
            tool_type: toolKey,
            name: toolConfigs[toolKey]?.name || toolKey,
            icon: toolConfigs[toolKey]?.icon,
            color: toolConfigs[toolKey]?.color
          }));
        setSpaceTools(tools);
      }
    };

    if (isGlobalView || projectId || isStackView) {
      fetchProject();
    }
  }, [projectId, isStackView, isGlobalView]);

  // Fetch all available spaces for the user
  useEffect(() => {
    const fetchAllSpaces = async () => {
      if (!userProfile?.id) return;

      try {
        // Fetch all spaces where the user is a member
        const { data, error } = await supabase
          .from('space_members')
          .select(`
            project_id,
            projects:project_id (
              id,
              name
            )
          `)
          .eq('user_id', userProfile.id);

        if (error) {
          console.error('Error fetching available spaces:', error);
          return;
        }

        // Extract unique projects
        const uniqueSpaces = [];
        const seenIds = new Set();

        data?.forEach(member => {
          if (member.projects && !seenIds.has(member.projects.id)) {
            seenIds.add(member.projects.id);
            uniqueSpaces.push(member.projects);
          }
        });

        setAllAvailableSpaces(uniqueSpaces);
      } catch (error) {
        console.error('Error fetching available spaces:', error);
      }
    };

    fetchAllSpaces();
  }, [userProfile?.id]);

  // Note: Posts fetching is now handled by usePostsData hook above

  // Helper function to select a post and update URL
  const handleSelectPost = useCallback((post) => {
    setSelectedPost(post);

    // Update URL based on view type
    if (isGlobalView) {
      navigate(`/posts/${post.id}`, { replace: false });
    } else if (isStackView && stackId) {
      navigate(`/stack/${stackId}/message-board/${post.id}`, { replace: false });
    } else if (projectId) {
      navigate(`/space/${projectId}/message-board/${post.id}`, { replace: false });
    }
  }, [navigate, isGlobalView, isStackView, stackId, projectId]);

  // Helper function to close post and clear URL
  const handleClosePost = useCallback(() => {
    setSelectedPost(null);

    // Navigate back to message board list
    if (isGlobalView) {
      navigate(`/posts`, { replace: false });
    } else if (isStackView && stackId) {
      navigate(`/stack/${stackId}/message-board`, { replace: false });
    } else if (projectId) {
      navigate(`/space/${projectId}/message-board`, { replace: false });
    }
  }, [navigate, isGlobalView, isStackView, stackId, projectId]);

  // Handle postId from URL - open that specific post
  useEffect(() => {
    if (postId && posts.length > 0 && !selectedPost) {
      const post = posts.find(p => p.id === postId);
      if (post) {
        setSelectedPost(post);
        // Scroll to the post card
        setTimeout(() => {
          const postElement = document.querySelector(`[data-post-id="${postId}"]`);
          if (postElement) {
            postElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
    } else if (!postId && selectedPost) {
      // URL doesn't have postId but we have a selected post - clear it
      setSelectedPost(null);
    }
  }, [postId, posts, selectedPost]);

  // When a post is selected, fetch its details (comments, tracking) using optimized RPC
  useEffect(() => {
    const loadPostDetails = async () => {
      if (!selectedPost) {
        setComments([]);
        setUserTracking(null);
        setTrackingStats(null);
        setAllTracking([]);
        return;
      }

      // Use the optimized RPC to get all post details in one call
      const detail = await fetchPostDetail(selectedPost.id, true);

      if (detail) {
        setComments(detail.comments || []);
        setUserTracking(detail.userTracking || null);
        setTrackingStats(detail.trackingStats || null);
        setAllTracking(detail.allTracking || []);
      } else {
        setComments([]);
        setUserTracking(null);
        setTrackingStats(null);
        setAllTracking([]);
      }
    };

    loadPostDetails();
  }, [selectedPost, fetchPostDetail]);

  // Preserve scroll position during recording timer updates
  useEffect(() => {
    if (recordingState === 'recording' || recordingState === 'paused') {
      // Store scroll position before re-render
      const scrollContainer = dialogScrollRef.current;
      if (scrollContainer) {
        const scrollTop = scrollContainer.scrollTop;
        // Restore scroll position after re-render
        requestAnimationFrame(() => {
          if (scrollContainer) {
            scrollContainer.scrollTop = scrollTop;
          }
        });
      }
    }
  }, [recordingTimer, recordingState]);

  const handleImageUpload = async e => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingImage(true);

    try {
      // Create unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()
        .toString(36)
        .substring(2)}-${Date.now()}.${fileExt}`;

      // Upload to GCS
      const { publicUrl } = await gcsUploadFile(file, {
        folderPath: `project-files/message-board/${projectId}`,
        fileName: fileName,
        contentType: file.type
      });

      setFormData({ ...formData, imageUrl: publicUrl });
      setImagePreview(URL.createObjectURL(file));
      toast.success('Image uploaded');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = () => {
    setFormData({ ...formData, imageUrl: '', imageCaption: '' });
    setImagePreview(null);
  };

  const handleVideoUpload = async e => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('video/')) {
      toast.error('Please upload a video file');
      return;
    }

    // Validate file size (50MB max for videos)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Video must be less than 50MB');
      return;
    }

    setUploadingVideo(true);

    try {
      // Create unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()
        .toString(36)
        .substring(2)}-${Date.now()}.${fileExt}`;

      // Upload to GCS
      const { publicUrl } = await gcsUploadFile(file, {
        folderPath: `project-files/message-board/${projectId}`,
        fileName: fileName,
        contentType: file.type
      });

      setFormData({ ...formData, videoUrl: publicUrl });
      setVideoPreview(URL.createObjectURL(file));
      toast.success('Video uploaded');
    } catch (error) {
      console.error('Error uploading video:', error);
      toast.error('Failed to upload video');
    } finally {
      setUploadingVideo(false);
    }
  };

  const removeVideo = () => {
    setFormData({ ...formData, videoUrl: '' });
    setVideoPreview(null);
    closeRecordingStudio();
  };

  // Open recording studio (camera preview but not recording yet)
  const openRecordingStudio = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true
      });

      setMediaStream(stream);
      setRecordingState('preview');
      toast.success('Camera ready');
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast.error('Failed to access camera/microphone. Please check permissions.');
      setRecordingState('idle');
    }
  };

  // Start recording
  const startRecording = () => {
    if (!mediaStream) return;

    try {
      const chunks = [];
      const recorder = new MediaRecorder(mediaStream, {
        mimeType: MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
          ? 'video/webm;codecs=vp9'
          : 'video/webm'
      });

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/webm' });
        setRecordedBlob(blob);
        setRecordingState('reviewing');

        // Stop timer
        if (timerInterval) {
          clearInterval(timerInterval);
          setTimerInterval(null);
        }
      };

      setMediaRecorder(recorder);
      setRecordedChunks(chunks);
      recorder.start(100); // Collect data every 100ms
      setRecordingState('recording');
      setRecordingTimer(0);

      // Start timer
      const interval = setInterval(() => {
        setRecordingTimer(prev => prev + 1);
      }, 1000);
      setTimerInterval(interval);

      toast.success('Recording started');
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Failed to start recording');
    }
  };

  // Pause recording
  const pauseRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.pause();
      setRecordingState('paused');
      if (timerInterval) {
        clearInterval(timerInterval);
        setTimerInterval(null);
      }
      toast.info('Recording paused');
    }
  };

  // Resume recording
  const resumeRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'paused') {
      mediaRecorder.resume();
      setRecordingState('recording');

      // Resume timer
      const interval = setInterval(() => {
        setRecordingTimer(prev => prev + 1);
      }, 1000);
      setTimerInterval(interval);

      toast.success('Recording resumed');
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }
  };

  // Discard recording and start over
  const discardRecording = () => {
    setRecordedBlob(null);
    setRecordingTimer(0);
    setRecordedChunks([]);
    setRecordingState('preview');
    toast.info('Recording discarded');
  };

  // Use the recorded video (upload it)
  const useRecording = async () => {
    if (!recordedBlob) return;

    setUploadingVideo(true);
    try {
      const fileName = `${Math.random()
        .toString(36)
        .substring(2)}-${Date.now()}.webm`;

      const { publicUrl } = await gcsUploadFile(recordedBlob, {
        folderPath: `project-files/message-board/${projectId}`,
        fileName: fileName,
        contentType: 'video/webm'
      });

      setFormData({ ...formData, videoUrl: publicUrl });
      setVideoPreview(URL.createObjectURL(recordedBlob));
      closeRecordingStudio();
      toast.success('Video uploaded successfully');
    } catch (error) {
      console.error('Error uploading video:', error);
      toast.error('Failed to upload video');
    } finally {
      setUploadingVideo(false);
    }
  };

  // Close recording studio and clean up
  const closeRecordingStudio = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }
    if (mediaStream) {
      mediaStream.getTracks().forEach(track => track.stop());
      setMediaStream(null);
    }
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }
    setMediaRecorder(null);
    setRecordedChunks([]);
    setRecordedBlob(null);
    setRecordingTimer(0);
    setRecordingState('idle');
  };

  const createPost = async () => {
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    // Validate based on post type
    if (postType === 'text' && !formData.content.trim()) {
      toast.error('Please enter content');
      return;
    }
    if (postType === 'link' && !formData.linkUrl.trim()) {
      toast.error('Please enter a link URL');
      return;
    }
    if (postType === 'image' && !formData.imageUrl) {
      toast.error('Please upload an image');
      return;
    }
    if (postType === 'video' && !formData.videoUrl) {
      toast.error('Please upload a video');
      return;
    }

    // Validate based on visibility type
    if (visibilityType === 'space' && (!formData.selectedProjectIds || formData.selectedProjectIds.length === 0)) {
      toast.error('Please select at least one space');
      return;
    }

    // Build post data based on visibility type
    const postData = {
      title: formData.title.trim(),
      content: formData.content.trim(),
      created_by: userProfile.id,
      company_id: company.id,
      post_type: postType,
      link_url: postType === 'link' ? formData.linkUrl.trim() : null,
      image_url: postType === 'image' ? formData.imageUrl : null,
      image_caption: postType === 'image' ? formData.imageCaption.trim() : null,
      video_url: postType === 'video' ? formData.videoUrl : null,
      selected_project_ids: [],
      requires_reading: formData.requiresReading,
      requires_understanding: formData.requiresUnderstanding
    };

    // Set visibility fields based on type
    if (visibilityType === 'company') {
      // Company-level post
      postData.project_id = null;
      postData.selected_project_ids = [];
    } else if (visibilityType === 'space') {
      // Space post (single or multiple)
      const selectedIds = formData.selectedProjectIds || [];
      if (selectedIds.length === 1) {
        // Single space - use project_id for backwards compatibility
        postData.project_id = selectedIds[0];
        postData.selected_project_ids = [];
      } else {
        // Multiple spaces - use selected_project_ids
        postData.project_id = null;
        postData.selected_project_ids = selectedIds;
      }
    }

    const { data: newPost, error } = await supabase
      .from('message_board_posts')
      .insert([postData])
      .select()
      .single();

    if (error) {
      console.error('Error creating post:', error);
      toast.error('Failed to create post');
      return;
    }

    // If tracking is required, create tracking records for eligible users
    if (formData.requiresReading || formData.requiresUnderstanding || quizData) {
      try {
        let eligibleUserIds = [];

        if (visibilityType === 'company') {
          // Fetch all users in company
          const { data: companyMembers } = await supabase
            .from('company_members')
            .select('user_id')
            .eq('company_id', company.id);

          eligibleUserIds = companyMembers?.map(m => m.user_id) || [];
        } else if (visibilityType === 'space') {
          // Fetch users in any of the selected spaces (deduplicated)
          const { data: projectMembers } = await supabase
            .from('space_members')
            .select('user_id')
            .in('project_id', formData.selectedProjectIds);

          // Deduplicate user IDs
          eligibleUserIds = [...new Set(projectMembers?.map(m => m.user_id) || [])];
        }

        // Create tracking records for all eligible users
        if (eligibleUserIds.length > 0) {
          const trackingRecords = eligibleUserIds.map(userId => ({
            post_id: newPost.id,
            user_id: userId
          }));

          const { error: trackingError } = await supabase
            .from('message_board_post_tracking')
            .insert(trackingRecords);

          if (trackingError) {
            console.error('Error creating tracking records:', trackingError);
            // Don't fail the post creation if tracking fails
          }

          // Create notifications for eligible users (excluding post creator)
          const notificationUserIds = eligibleUserIds.filter(
            userId => userId !== userProfile.id
          );

          if (notificationUserIds.length > 0) {
            // Determine link URL based on visibility type
            let linkUrl = '/dashboard'; // Default fallback
            if (visibilityType === 'space' && formData.selectedProjectId) {
              linkUrl = `/space/${formData.selectedProjectId}/message-board`;
            } else if (visibilityType === 'stack' && stackId) {
              linkUrl = `/stack/${stackId}/message-board`;
            } else if (visibilityType === 'company' && projectId) {
              // For company-wide, use the current project's message board if available
              linkUrl = `/space/${projectId}/message-board`;
            }

            // Build notification title and message
            const requirementText = formData.requiresUnderstanding
              ? 'requires your understanding'
              : 'requires reading';

            await sendNotification({
              notification_type: 'assignments',
              user_ids: notificationUserIds,
              title: 'New post requires your attention',
              message: `${userProfile.first_name || ''} ${userProfile.last_name || ''} posted "${formData.title}" which ${requirementText}`,
              space_id: formData.selectedProjectId || null,
              link_url: linkUrl,
              entity_type: 'message_board_post',
              entity_id: newPost.id,
              actor_id: userProfile.id,
              group_key: `message_board_post_${newPost.id}`
            });
          }
        }
      } catch (trackingError) {
        console.error('Error setting up post tracking:', trackingError);
        // Don't fail the post creation if tracking fails
      }
    }

    toast.success('Post created successfully');
    setIsCreateDialogOpen(false);
    setFormData({
      title: '',
      content: '',
      linkUrl: '',
      imageUrl: '',
      imageCaption: '',
      selectedProjectId:
        isStackView && stackProjects?.[0]?.id ? stackProjects[0].id : projectId,
      selectedProjectIds: isStackView ? stackProjects?.map(p => p.id) || [] : [],
      requiresReading: false,
      requiresUnderstanding: false
    });
    setPostType('text');
    setVisibilityType(isStackView ? 'stack' : 'space');
    setImagePreview(null);
    refetchPosts();
  };

  const updatePost = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    // Validate space selection
    if (visibilityType === 'space' && (!formData.selectedProjectIds || formData.selectedProjectIds.length === 0)) {
      toast.error('Please select at least one space');
      return;
    }

    // Prepare update data
    const updateData = {
      title: formData.title.trim(),
      content: formData.content.trim(),
      updated_date: new Date().toISOString()
    };

    // Set visibility based on type
    if (visibilityType === 'company') {
      // All Spaces post
      updateData.project_id = null;
      updateData.selected_project_ids = [];
    } else if (visibilityType === 'space') {
      // Space post (single or multiple)
      const selectedIds = formData.selectedProjectIds || [];
      if (selectedIds.length === 1) {
        // Single space - use project_id for backwards compatibility
        updateData.project_id = selectedIds[0];
        updateData.selected_project_ids = [];
      } else {
        // Multiple spaces - use selected_project_ids
        updateData.project_id = null;
        updateData.selected_project_ids = selectedIds;
      }
    }

    const { error } = await supabase
      .from('message_board_posts')
      .update(updateData)
      .eq('id', editingPost.id);

    if (error) {
      console.error('Error updating post:', error);
      toast.error('Failed to update post');
      return;
    }

    toast.success('Post updated successfully');
    setIsEditDialogOpen(false);
    setEditingPost(null);
    setFormData({ title: '', content: '' });
    refetchPosts();
    if (selectedPost?.id === editingPost.id) {
      handleClosePost();
    }
  };

  const deletePost = useCallback(async (postId) => {
    if (!confirm('Are you sure you want to delete this post?')) return;

    const { error } = await supabase
      .from('message_board_posts')
      .delete()
      .eq('id', postId);

    if (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
      return;
    }

    toast.success('Post deleted successfully');
    if (selectedPost?.id === postId) {
      handleClosePost();
    }
    removePostLocally(postId);
  }, [selectedPost?.id, handleClosePost, removePostLocally]);

  const togglePin = useCallback(async (post) => {
    const newPinnedState = !post.pinned;

    // Optimistically update the post in state
    updatePostLocally(post.id, { pinned: newPinnedState });

    // Update in database
    const { error } = await supabase
      .from('message_board_posts')
      .update({ pinned: newPinnedState })
      .eq('id', post.id);

    if (error) {
      console.error('Error toggling pin:', error);
      toast.error('Failed to update post');

      // Revert the optimistic update on error
      updatePostLocally(post.id, { pinned: !newPinnedState });
      return;
    }

    toast.success(newPinnedState ? 'Post pinned' : 'Post unpinned');
    // Refetch to get proper sort order
    refetchPosts();
  }, [updatePostLocally, refetchPosts]);

  const addComment = async () => {
    if (!newComment.trim()) return;

    const commentContent = newComment.trim();
    const tempId = `temp-${Date.now()}`;

    // Create optimistic comment
    const optimisticComment = {
      id: tempId,
      post_id: selectedPost.id,
      content: commentContent,
      created_by: userProfile.id,
      created_date: new Date().toISOString(),
      author: {
        id: userProfile.id,
        first_name: userProfile.first_name,
        last_name: userProfile.last_name,
        display_name: userProfile.display_name,
        avatar_url: userProfile.avatar_url
      }
    };

    // Immediately update UI
    setComments(prev => [...prev, optimisticComment]);
    setNewComment('');

    // Update post comment count optimistically
    const currentCount = selectedPost.comments?.count || selectedPost.comments?.[0]?.count || 0;
    updatePostLocally(selectedPost.id, { comments: { count: currentCount + 1 } });

    // Save to database in background
    const { data, error } = await supabase
      .from('message_board_comments')
      .insert([
        {
          post_id: selectedPost.id,
          content: commentContent,
          created_by: userProfile.id
        }
      ])
      .select(
        `
        *,
        author:created_by(id, first_name, last_name, display_name, avatar_url)
      `
      )
      .single();

    if (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
      // Revert optimistic update
      setComments(prev => prev.filter(c => c.id !== tempId));
      updatePostLocally(selectedPost.id, { comments: { count: Math.max(0, currentCount) } });
      return;
    }

    // Replace temp comment with real data
    setComments(prev => prev.map(c => (c.id === tempId ? data : c)));
  };

  const deleteComment = async commentId => {
    if (!confirm('Are you sure you want to delete this comment?')) return;

    // Store for potential rollback
    const deletedComment = comments.find(c => c.id === commentId);
    const currentCount = selectedPost.comments?.count || selectedPost.comments?.[0]?.count || 1;

    // Optimistically remove from UI
    setComments(prev => prev.filter(c => c.id !== commentId));

    // Update post comment count optimistically
    updatePostLocally(selectedPost.id, { comments: { count: Math.max(0, currentCount - 1) } });

    // Delete from database in background
    const { error } = await supabase
      .from('message_board_comments')
      .delete()
      .eq('id', commentId);

    if (error) {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
      // Revert optimistic update
      if (deletedComment) {
        setComments(prev =>
          [...prev, deletedComment].sort(
            (a, b) => new Date(a.created_date) - new Date(b.created_date)
          )
        );
        updatePostLocally(selectedPost.id, { comments: { count: currentCount } });
      }
      return;
    }

    toast.success('Comment deleted');
  };

  const handleUnderstand = async () => {
    if (!selectedPost || !userProfile?.id || !userTracking) return;

    try {
      const { data: updated, error } = await supabase
        .from('message_board_post_tracking')
        .update({ understood_date: new Date().toISOString() })
        .eq('post_id', selectedPost.id)
        .eq('user_id', userProfile.id)
        .select()
        .single();

      if (error) throw error;

      setUserTracking(updated);

      // Update the posts array to reflect the change
      updatePostLocally(selectedPost.id, { user_has_understood: true });

      // Update selectedPost as well
      setSelectedPost(prev => ({
        ...prev,
        user_has_understood: true
      }));

      toast.success('Confirmed understanding');
    } catch (error) {
      console.error('Error confirming understanding:', error);
      toast.error('Failed to confirm understanding');
    }
  };

  const handleOpenQuiz = () => {
    console.log('=== handleOpenQuiz START ===');
    console.log('Current isQuizModalOpen value:', isQuizModalOpen);

    // Check if it's already open and force close then reopen
    if (isQuizModalOpen) {
      console.log('Modal already open! Closing and reopening...');
      setIsQuizModalOpen(false);
      setTimeout(() => {
        setIsQuizModalOpen(true);
        setQuizAnswers({});
        setQuizSubmitted(false);
        setQuizScore(null);
      }, 100);
    } else {
      console.log('Opening modal for first time');
      setIsQuizModalOpen(true);
      setQuizAnswers({});
      setQuizSubmitted(false);
      setQuizScore(null);
    }
    console.log('=== handleOpenQuiz END ===');
  };

  const handleQuizAnswer = (questionIndex, answer) => {
    setQuizAnswers(prev => ({
      ...prev,
      [questionIndex]: answer
    }));
  };

  const handleSubmitQuiz = async () => {
    if (!selectedPost?.quiz_data || !userProfile?.id) return;

    const startTime = Date.now();
    let correct = 0;
    const totalQuestions = selectedPost.quiz_data.questions.length;
    const answerDetails = [];

    selectedPost.quiz_data.questions.forEach((question, index) => {
      const userAnswer = quizAnswers[index];
      let isCorrect = false;

      if (question.type === 'multiple_choice') {
        isCorrect = userAnswer === question.correct_answer;
      } else if (question.type === 'true_false') {
        // Handle boolean correctAnswer (true/false) vs string userAnswer ("True"/"False")
        const normalizedCorrectAnswer = typeof question.correct_answer === 'boolean'
          ? (question.correct_answer ? 'True' : 'False')
          : question.correct_answer;
        isCorrect = userAnswer === normalizedCorrectAnswer;
      } else if (question.type === 'short_answer') {
        // For short answer, we'll be lenient with case and whitespace
        const normalizedUserAnswer = userAnswer?.toLowerCase().trim();
        const normalizedCorrectAnswer = question.correct_answer?.toLowerCase().trim();
        isCorrect = normalizedUserAnswer === normalizedCorrectAnswer;
      }

      if (isCorrect) correct++;

      answerDetails.push({
        question: question.question,
        userAnswer: userAnswer,
        correctAnswer: question.correct_answer,
        isCorrect: isCorrect
      });
    });

    const score = Math.round((correct / totalQuestions) * 100);
    const timeTaken = Math.floor((Date.now() - startTime) / 1000);

    setQuizScore(score);
    setQuizSubmitted(true);

    // Save quiz results to database
    try {
      const { error } = await supabase
        .from('message_board_post_tracking')
        .update({
          quiz_completed_date: new Date().toISOString(),
          quiz_score: score,
          quiz_answers: answerDetails,
          quiz_time_taken: timeTaken
        })
        .eq('post_id', selectedPost.id)
        .eq('user_id', userProfile.id);

      if (error) {
        console.error('Error saving quiz results:', error);
      }
    } catch (error) {
      console.error('Error saving quiz results:', error);
    }

    if (score >= 70) {
      toast.success(`Great job! You scored ${score}%`);
    } else {
      toast.error(`You scored ${score}%. Try again to improve!`);
    }
  };

  const handleRetakeQuiz = () => {
    setQuizAnswers({});
    setQuizSubmitted(false);
    setQuizScore(null);
  };

  // Get space names for a post's selected_project_ids, filtered by user's access
  const getAccessibleSpaceNames = useCallback((selectedProjectIds) => {
    if (!selectedProjectIds || selectedProjectIds.length === 0) return [];

    // Filter to only include spaces the user has access to
    return allAvailableSpaces
      .filter(space => selectedProjectIds.includes(space.id))
      .map(space => space.name);
  }, [allAvailableSpaces]);

  // Component for displaying multi-space badge with tooltip
  const MultiSpaceBadge = ({ selectedProjectIds }) => {
    const accessibleSpaces = getAccessibleSpaceNames(selectedProjectIds);
    const totalCount = selectedProjectIds?.length || 0;
    const accessibleCount = accessibleSpaces.length;

    if (totalCount === 0) return null;

    return (
      <TooltipProvider>
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <Badge variant='outline' className='gap-1 cursor-pointer'>
              <Layers className='h-3 w-3' />
              {totalCount} Space{totalCount !== 1 ? 's' : ''}
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs">
            <div className="space-y-1">
              <p className="font-semibold text-xs mb-1.5">Posted to:</p>
              {accessibleSpaces.length > 0 ? (
                <ul className="space-y-0.5">
                  {accessibleSpaces.map((spaceName, index) => (
                    <li key={index} className="text-xs flex items-center gap-1.5">
                      <Grid3X3 className="h-3 w-3 flex-shrink-0" />
                      <span>{spaceName}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-muted-foreground">
                  No accessible spaces
                </p>
              )}
              {accessibleCount < totalCount && (
                <p className="text-xs text-muted-foreground mt-2 pt-1.5 border-t">
                  +{totalCount - accessibleCount} space{totalCount - accessibleCount !== 1 ? 's' : ''} you don't have access to
                </p>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  // Client-side filtering for global view space filters (search is handled server-side by usePostsData hook)
  const filteredPosts = useMemo(() => {
    let filtered = posts;

    // Apply space filter in global view
    if (isGlobalView && selectedSpaceFilters.length > 0) {
      filtered = filtered.filter(post => {
        // Company-wide posts (no project_id and no selected_project_ids) - always show
        if (!post.project_id && (!post.selected_project_ids || post.selected_project_ids.length === 0)) {
          return true;
        }

        // Single space posts
        if (post.project_id) {
          return selectedSpaceFilters.includes(post.project_id);
        }

        // Multi-space posts
        if (!post.project_id && post.selected_project_ids && post.selected_project_ids.length > 0) {
          return post.selected_project_ids.some(id => selectedSpaceFilters.includes(id));
        }

        return false;
      });
    }

    return filtered;
  }, [posts, isGlobalView, selectedSpaceFilters]);

  const startEditPost = useCallback((post) => {
    setEditingPost(post);

    // Determine visibility type and selected spaces
    let visType = 'company';
    let selectedIds = [];

    if (post.project_id) {
      // Single space post
      visType = 'space';
      selectedIds = [post.project_id];
    } else if (post.selected_project_ids && post.selected_project_ids.length > 0) {
      // Multi-space post
      visType = 'space';
      selectedIds = post.selected_project_ids;
    }

    setVisibilityType(visType);
    setFormData({
      title: post.title,
      content: post.content,
      selectedProjectIds: selectedIds,
      selectedProjectId: selectedIds.length === 1 ? selectedIds[0] : null
    });
    setIsEditDialogOpen(true);
  }, []);

  if (loading) {
    if (isGlobalView) {
      return (
        <>
          <SharedNavbar isGlobalToolsOpen={globalToolsOpen} onGlobalToolsToggle={handleGlobalToolsToggle} />
          <GlobalToolsExpandingPanel isOpen={globalToolsOpen} onClose={() => setGlobalToolsOpen(false)} currentTool="posts" />
          <div className='flex items-center justify-center h-screen'>
            <LoadingSpinner />
          </div>
        </>
      );
    }
    return (
      <AppShell3 project={project}>
        <div className='flex items-center justify-center h-64'>
          <LoadingSpinner />
        </div>
      </AppShell3>
    );
  }

  // Show post detail view
  if (selectedPost) {
    const detailContent = (
      <PageTransition>
        <ToolHeader
          spaceName={isGlobalView ? 'Company' : (isStackView ? stackName : space?.name || 'Space')}
          spaceImage={isGlobalView ? null : (isStackView ? stackImage : space?.logo_url)}
          toolName='Posts'
          currentTool='posts'
          spaceTools={isGlobalView ? globalTools : spaceTools}
          stackId={stackId}
          isStackView={isStackView}
          isGlobalView={isGlobalView}
        />
        <PageFadeIn>
          <div className={`${selectedPost?.video_url && selectedPost?.transcription_timestamps ? 'max-w-7xl' : 'max-w-4xl'} mx-auto px-6 py-6 space-y-6`}>
              {/* Back button */}
              <Button
                variant='ghost'
                onClick={handleClosePost}
                className='gap-2'
              >
                <ArrowLeft className='h-4 w-4' />
                Back to all posts
              </Button>

              {/* Post detail */}
              <Card>
                <CardHeader>
                  <div className='flex items-start justify-between gap-4'>
                    <div className='flex-1'>
                      <div className='flex items-center gap-2 mb-2 flex-wrap'>
                        {selectedPost.pinned && (
                          <Badge variant='secondary' className='gap-1'>
                            <Pin className='h-3 w-3' />
                            Pinned
                          </Badge>
                        )}
                        {/* Visibility Badge */}
                        {!selectedPost.project_id && (!selectedPost.selected_project_ids || selectedPost.selected_project_ids.length === 0) && (
                          <Badge variant='outline' className='gap-1'>
                            <Building2 className='h-3 w-3' />
                            All Spaces
                          </Badge>
                        )}
                        {selectedPost.project_id && (
                          <Badge variant='outline' className='gap-1'>
                            <Grid3X3 className='h-3 w-3' />
                            {selectedPost.projects?.name || 'Single Space'}
                          </Badge>
                        )}
                        {!selectedPost.project_id && selectedPost.selected_project_ids && selectedPost.selected_project_ids.length > 0 && (
                          <MultiSpaceBadge selectedProjectIds={selectedPost.selected_project_ids} />
                        )}
                      </div>
                      <CardTitle className='text-2xl mb-2'>
                        {selectedPost.title}
                      </CardTitle>
                      <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                        <Avatar className='h-6 w-6'>
                          <AvatarImage src={selectedPost.author?.avatar_url} />
                          <AvatarFallback>
                            <User className='h-3 w-3' />
                          </AvatarFallback>
                        </Avatar>
                        <span>
                          {selectedPost.author?.display_name ||
                            `${selectedPost.author?.first_name || ''} ${
                              selectedPost.author?.last_name || ''
                            }`.trim() ||
                            'Unknown'}
                        </span>
                        <span>•</span>
                        <span>
                          {formatDistanceToNow(
                            new Date(selectedPost.created_date),
                            { addSuffix: true }
                          )}
                        </span>
                      </div>
                    </div>
                    {userProfile &&
                      selectedPost.created_by === userProfile.id && (
                        <div className='flex items-center gap-2'>
                          {/* Tracking Button - Only show if post has tracking requirements */}
                          {(selectedPost.requires_reading || selectedPost.requires_understanding || selectedPost.has_quiz) && (
                            <Button
                              variant='outline'
                              size='sm'
                              onClick={() => navigate(projectId
                                ? `/space/${projectId}/message-board/${selectedPost.id}/tracking`
                                : `/posts/${selectedPost.id}/tracking`
                              )}
                            >
                              <BarChart3 className='h-4 w-4 mr-2' />
                              View Tracking
                            </Button>
                          )}

                          {/* Actions Dropdown */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant='ghost'
                                size='sm'
                                className='h-8 w-8 p-0'
                              >
                                <MoreVertical className='h-4 w-4' />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align='end'>
                              <DropdownMenuItem
                                onClick={() => startEditPost(selectedPost)}
                              >
                                <Edit className='h-4 w-4 mr-2' />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => deletePost(selectedPost.id)}
                                className='text-red-600'
                              >
                                <Trash2 className='h-4 w-4 mr-2' />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      )}
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Text Post */}
                  {selectedPost.post_type === 'text' && (
                    <TipTapContent content={selectedPost.content} className='text-sm' />
                  )}

                  {/* Link Post */}
                  {selectedPost.post_type === 'link' && (
                    <div className='space-y-3'>
                      <a
                        href={selectedPost.link_url}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='flex items-center gap-2 p-4 rounded-lg border hover:bg-muted/50 transition-colors group'
                      >
                        <ExternalLink className='h-5 w-5 text-muted-foreground group-hover:text-primary' />
                        <div className='flex-1 min-w-0'>
                          <p className='font-medium text-sm truncate group-hover:text-primary'>
                            {selectedPost.link_url}
                          </p>
                        </div>
                      </a>
                      {selectedPost.content && (
                        <TipTapContent
                          content={selectedPost.content}
                          className='text-sm text-muted-foreground'
                        />
                      )}
                    </div>
                  )}

                  {/* Image Post */}
                  {selectedPost.post_type === 'image' && (
                    <div className='space-y-3'>
                      <div className='rounded-lg overflow-hidden border'>
                        <img
                          src={selectedPost.image_url}
                          alt={selectedPost.title}
                          className='w-full h-auto max-h-[600px] object-contain bg-muted'
                        />
                      </div>
                      {selectedPost.image_caption && (
                        <p className='text-sm text-muted-foreground italic'>
                          {selectedPost.image_caption}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Video Post */}
                  {selectedPost.post_type === 'video' && (
                    <div className='space-y-3'>
                      {/* Two Column Layout: Video + Transcript */}
                      {selectedPost.transcription_timestamps ? (
                        <div className='flex flex-col lg:flex-row gap-4 relative items-start'>
                          {/* Video Column */}
                          <div className={`${isTranscriptVisible ? 'lg:w-[70%]' : 'lg:max-w-4xl lg:mx-auto'} w-full rounded-lg overflow-hidden border relative transition-all duration-300 ease-in-out`}>
                            <video
                              ref={videoRef}
                              src={selectedPost.video_url}
                              controls
                              preload='metadata'
                              className='w-full h-auto bg-muted'
                              onLoadedMetadata={(e) => {
                                setVideoDuration(e.target.duration);
                                // Update video height when metadata loads
                                requestAnimationFrame(() => {
                                  const height = e.target.offsetHeight;
                                  if (height > 0) {
                                    setVideoHeight(height);
                                  }
                                });
                              }}
                              onProgress={(e) => {
                                // Get the furthest buffered time range
                                const video = e.target;
                                if (video.buffered.length > 0) {
                                  const bufferedEnd = video.buffered.end(video.buffered.length - 1);
                                  setVideoBuffered(bufferedEnd);
                                }
                              }}
                              onTimeUpdate={(e) => {
                                setCurrentVideoTime(e.target.currentTime);

                                // Find active word based on current time
                                if (selectedPost.transcription_timestamps) {
                                  const index = selectedPost.transcription_timestamps.findIndex((word, idx) => {
                                    const nextWord = selectedPost.transcription_timestamps[idx + 1];
                                    return e.target.currentTime >= word.start &&
                                           (!nextWord || e.target.currentTime < nextWord.start);
                                  });
                                  setActiveWordIndex(index);
                                }
                              }}
                            />
                            {/* Toggle Transcript Button */}
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="secondary"
                                    size="icon"
                                    onClick={() => setIsTranscriptVisible(!isTranscriptVisible)}
                                    className="absolute top-4 right-4 h-9 w-9 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 z-10 bg-background/95 backdrop-blur-sm border-2"
                                  >
                                    {isTranscriptVisible ? (
                                      <ChevronRight className="h-5 w-5" />
                                    ) : (
                                      <ChevronLeft className="h-5 w-5" />
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent side="left">
                                  <p>{isTranscriptVisible ? 'Hide Transcript' : 'Show Transcript'}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>

                          {/* Transcript Column - 30% */}
                          <AnimatePresence>
                            {isTranscriptVisible && (
                              <motion.div
                                initial={{ opacity: 0, x: 30 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 30 }}
                                transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                                className='lg:w-[30%] w-full rounded-lg border bg-card flex flex-col'
                                style={{ height: `${videoHeight}px` }}
                              >
                            <div className='p-4 border-b bg-muted/50 flex-shrink-0'>
                              <div className='flex items-center gap-2'>
                                <FileText className='h-4 w-4 text-muted-foreground' />
                                <h3 className='font-semibold text-sm'>Transcription</h3>
                              </div>
                              <p className='text-xs text-muted-foreground mt-1'>
                                Click any word to jump to that moment
                              </p>
                            </div>
                            <div ref={transcriptScrollRef} className='p-4 bg-muted/30 overflow-y-auto flex-1'>
                              <div className='text-sm leading-relaxed'>
                                {selectedPost.transcription_timestamps.map((word, index) => (
                                  <span
                                    key={index}
                                    ref={activeWordIndex === index ? activeWordRef : null}
                                    onClick={() => {
                                      if (videoRef.current) {
                                        videoRef.current.currentTime = word.start;
                                        videoRef.current.play();
                                      }
                                    }}
                                    className={`cursor-pointer hover:bg-primary/20 px-0.5 rounded transition-colors ${
                                      activeWordIndex === index
                                        ? 'bg-primary text-primary-foreground font-medium'
                                        : ''
                                    }`}
                                    title={`Jump to ${word.start.toFixed(1)}s`}
                                  >
                                    {word.word}
                                  </span>
                                )).reduce((prev, curr, idx) => {
                                  // Add space between words
                                  return prev === null ? [curr] : [...prev, ' ', curr];
                                }, null)}
                              </div>
                            </div>
                            {selectedPost.metadata_url && (
                              <div className='p-4 border-t bg-muted/50 flex-shrink-0'>
                                <a
                                  href={selectedPost.metadata_url}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                  className='text-sm text-primary hover:underline flex items-center gap-1.5'
                                >
                                  <FileText className='h-3.5 w-3.5' />
                                  View Metadata
                                </a>
                              </div>
                            )}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      ) : (
                        // Fallback: Just video if no transcript
                        <div className='rounded-lg overflow-hidden border'>
                          <video
                            ref={videoRef}
                            src={selectedPost.video_url}
                            controls
                            className='w-full h-auto bg-muted'
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Document Post */}
                  {selectedPost.post_type === 'document' && (
                    <DocumentPostViewer
                      post={selectedPost}
                      formatFileSize={formatFileSize}
                      getFileIcon={getFileIcon}
                      getFileIconColor={getFileIconColor}
                    />
                  )}

                  {/* Policy Post */}
                  {selectedPost.post_type === 'policy' && (
                    <div className='space-y-4'>
                      <TipTapContent content={selectedPost.content} className='text-sm' />
                      {selectedPost.document_url && (
                        <DocumentPostViewer
                          post={selectedPost}
                          formatFileSize={formatFileSize}
                          getFileIcon={getFileIcon}
                          getFileIconColor={getFileIconColor}
                        />
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* I Understand Button */}
              {selectedPost.requires_understanding && userTracking && !userTracking.understood_date && (
                <Card className='bg-primary/5 border-primary/20'>
                  <CardContent className='pt-6'>
                    <div className='flex flex-col items-center gap-3 text-center'>
                      <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                        <CheckSquare className='h-4 w-4' />
                        <span>This post requires confirmation of understanding</span>
                      </div>
                      <Button onClick={handleUnderstand} className='gap-2'>
                        <CheckSquare className='h-4 w-4' />
                        I Understand
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Fallback: Raw Transcription (if no timestamps available) */}
              {selectedPost.video_url && selectedPost.transcription_raw && !selectedPost.transcription_timestamps && (
                <Card>
                  <CardHeader>
                    <CardTitle className='flex items-center gap-2'>
                      <FileText className='h-5 w-5' />
                      Transcription
                    </CardTitle>
                    <CardDescription>
                      Auto-generated transcript from video
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className='bg-muted p-4 rounded-lg max-h-[400px] overflow-y-auto'>
                      <p className='text-sm whitespace-pre-wrap leading-relaxed'>
                        {selectedPost.transcription_raw}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Quiz Section */}
              {selectedPost.has_quiz && selectedPost.quiz_data && (
                <Card className='bg-blue-50 border-blue-200'>
                  <CardHeader>
                    <CardTitle className='flex items-center gap-2'>
                      <GraduationCap className='h-5 w-5' />
                      {selectedPost.quiz_data.quizTitle || 'Quiz'}
                    </CardTitle>
                    <CardDescription>
                      {selectedPost.quiz_data.quizDescription}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {!quizSubmitted && (!userTracking?.quiz_completed_date || (userTracking?.quiz_score < 70)) ? (
                      <div className='space-y-6'>
                        {selectedPost.quiz_data.questions?.map((question, index) => (
                          <div key={index} className='space-y-3 p-4 border rounded-lg bg-white'>
                            <div className='font-medium text-sm text-gray-600'>
                              Question {index + 1} of {selectedPost.quiz_data.questions.length}
                            </div>
                            <div className='font-semibold'>{question.question}</div>

                            {question.type === 'multiple_choice' && (
                              <div className='space-y-2'>
                                {question.options?.map((option, optIndex) => (
                                  <div key={optIndex} className='flex items-center space-x-2'>
                                    <input
                                      type='radio'
                                      id={`q${index}-opt${optIndex}`}
                                      name={`question-${index}`}
                                      value={option}
                                      checked={quizAnswers[index] === option}
                                      onChange={() => handleQuizAnswer(index, option)}
                                      className='w-4 h-4'
                                    />
                                    <label htmlFor={`q${index}-opt${optIndex}`} className='cursor-pointer text-sm'>
                                      {option}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            )}

                            {question.type === 'true_false' && (
                              <div className='space-y-2'>
                                {['True', 'False'].map((option) => (
                                  <div key={option} className='flex items-center space-x-2'>
                                    <input
                                      type='radio'
                                      id={`q${index}-${option}`}
                                      name={`question-${index}`}
                                      value={option}
                                      checked={quizAnswers[index] === option}
                                      onChange={() => handleQuizAnswer(index, option)}
                                      className='w-4 h-4'
                                    />
                                    <label htmlFor={`q${index}-${option}`} className='cursor-pointer text-sm'>
                                      {option}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            )}

                            {question.type === 'short_answer' && (
                              <Input
                                placeholder='Type your answer...'
                                value={quizAnswers[index] || ''}
                                onChange={(e) => handleQuizAnswer(index, e.target.value)}
                              />
                            )}
                          </div>
                        ))}

                        <Button
                          onClick={handleSubmitQuiz}
                          disabled={Object.keys(quizAnswers).length !== selectedPost.quiz_data.questions?.length}
                          className='w-full bg-blue-600 hover:bg-blue-700'
                        >
                          Submit Quiz
                        </Button>
                      </div>
                    ) : (
                      <div className='space-y-6'>
                        {(() => {
                          // Use state quiz score if just submitted, otherwise use database score
                          const displayScore = quizScore !== null ? quizScore : (userTracking?.quiz_score || 0);
                          const displayAnswers = Object.keys(quizAnswers).length > 0 ? quizAnswers : null;
                          const savedAnswers = userTracking?.quiz_answers || [];
                          const justSubmitted = quizSubmitted; // Track if this is a fresh submission
                          const hasPassed = displayScore >= 70;
                          const showAnswers = justSubmitted || !hasPassed; // Only show answers if just submitted or failed

                          return (
                            <>
                              <div className='text-center space-y-4 p-6 bg-white rounded-lg'>
                                <div className={`text-6xl font-bold ${hasPassed ? 'text-green-600' : 'text-orange-600'}`}>
                                  {displayScore}%
                                </div>
                                <div className='text-lg font-semibold'>
                                  {hasPassed ? 'Congratulations! You passed!' : 'Keep trying!'}
                                </div>
                                <p className='text-sm text-gray-600'>
                                  You got {Math.round((displayScore / 100) * selectedPost.quiz_data.questions.length)} out of {selectedPost.quiz_data.questions.length} questions correct
                                </p>
                              </div>

                              {showAnswers && (
                              <div className='space-y-3'>
                                {selectedPost.quiz_data.questions?.map((question, index) => {
                                  // Get user answer from either current submission or saved answers
                                  let userAnswer, isCorrect;

                                  if (displayAnswers) {
                                    // Just submitted - use state
                                    userAnswer = displayAnswers[index];
                                  } else if (savedAnswers.length > 0) {
                                    // Previously completed - use saved data
                                    const savedAnswer = savedAnswers.find(a => a.question === question.question);
                                    userAnswer = savedAnswer?.userAnswer;
                                    isCorrect = savedAnswer?.isCorrect;
                                  }

                                  // Format the correct answer for display (handle boolean true/false)
                                  let displayCorrectAnswer = question.correct_answer;
                                  if (question.type === 'true_false' && typeof question.correct_answer === 'boolean') {
                                    displayCorrectAnswer = question.correct_answer ? 'True' : 'False';
                                  }

                                  // Determine if answer is correct (if not already determined from saved data)
                                  if (isCorrect === undefined && userAnswer !== undefined) {
                                    if (question.type === 'short_answer') {
                                      isCorrect = userAnswer?.toLowerCase().trim() === question.correct_answer?.toLowerCase().trim();
                                    } else if (question.type === 'true_false') {
                                      // Handle boolean correctAnswer (true/false) vs string userAnswer ("True"/"False")
                                      isCorrect = userAnswer === displayCorrectAnswer;
                                    } else {
                                      // multiple_choice
                                      isCorrect = userAnswer === question.correct_answer;
                                    }
                                  }

                                  return (
                              <div
                                key={index}
                                className={`p-4 border rounded-lg ${
                                  isCorrect ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                                }`}
                              >
                                <div className='flex items-start gap-2'>
                                  {isCorrect ? (
                                    <Check className='h-5 w-5 text-green-600 mt-0.5' />
                                  ) : (
                                    <X className='h-5 w-5 text-red-600 mt-0.5' />
                                  )}
                                  <div className='flex-1 space-y-2'>
                                    <div className='font-medium'>{question.question}</div>
                                    <div className='text-sm'>
                                      <span className='font-medium'>Your answer: </span>
                                      <span className={isCorrect ? 'text-green-700' : 'text-red-700'}>
                                        {userAnswer || 'No answer'}
                                      </span>
                                    </div>
                                    {!isCorrect && (
                                      <div className='text-sm'>
                                        <span className='font-medium'>Correct answer: </span>
                                        <span className='text-green-700'>{displayCorrectAnswer}</span>
                                      </div>
                                    )}
                                    {question.explanation && (
                                      <div className='text-sm text-gray-600 mt-2 pt-2 border-t'>
                                        <span className='font-medium'>Explanation: </span>
                                        {question.explanation}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                                })}
                              </div>
                              )}

                              {!hasPassed && (
                                <Button onClick={handleRetakeQuiz} className='w-full bg-blue-600 hover:bg-blue-700'>
                                  Retake Quiz
                                </Button>
                              )}
                              {hasPassed && (
                                <div className='text-center p-4 bg-green-50 border border-green-200 rounded-lg'>
                                  <p className='text-sm text-green-700 font-medium'>
                                    🎉 You've passed this quiz! Great job!
                                  </p>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Comments section */}
              <Card>
                <CardHeader>
                  <CardTitle className='flex items-center gap-2'>
                    <MessageSquare className='h-5 w-5' />
                    Comments ({comments.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className='space-y-4'>
                  {/* Comment list */}
                  <AnimatePresence mode='popLayout'>
                    {comments.map(comment => (
                      <motion.div
                        key={comment.id}
                        layout
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className='flex gap-3 p-4 rounded-lg bg-muted/50'
                      >
                        <Avatar className='h-8 w-8 flex-shrink-0'>
                          <AvatarImage src={comment.author?.avatar_url} />
                          <AvatarFallback>
                            <User className='h-4 w-4' />
                          </AvatarFallback>
                        </Avatar>
                        <div className='flex-1 min-w-0'>
                          <div className='flex items-center justify-between gap-2 mb-1'>
                            <div className='flex items-center gap-2 text-sm'>
                              <span className='font-medium'>
                                {comment.author?.display_name ||
                                  `${comment.author?.first_name || ''} ${
                                    comment.author?.last_name || ''
                                  }`.trim() ||
                                  'Unknown'}
                              </span>
                              <span className='text-muted-foreground'>
                                {formatDistanceToNow(
                                  new Date(comment.created_date),
                                  { addSuffix: true }
                                )}
                              </span>
                            </div>
                            {userProfile &&
                              comment.created_by === userProfile.id && (
                                <Button
                                  variant='ghost'
                                  size='sm'
                                  onClick={() => deleteComment(comment.id)}
                                  className='h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50'
                                >
                                  <Trash2 className='h-3 w-3' />
                                </Button>
                              )}
                          </div>
                          <p className='text-sm whitespace-pre-wrap'>
                            {comment.content}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>

                  {comments.length === 0 && (
                    <div className='text-center py-8 text-muted-foreground'>
                      <MessageSquare className='h-12 w-12 mx-auto mb-2 opacity-50' />
                      <p>No comments yet. Be the first to comment!</p>
                    </div>
                  )}

                  {/* Add comment form */}
                  <div className='flex gap-2 pt-2 border-t'>
                    <Avatar className='h-8 w-8 flex-shrink-0'>
                      <AvatarImage src={userProfile?.avatar_url} />
                      <AvatarFallback>
                        <User className='h-4 w-4' />
                      </AvatarFallback>
                    </Avatar>
                    <div className='flex-1 flex gap-2'>
                      <Textarea
                        placeholder='Write a comment...'
                        value={newComment}
                        onChange={e => setNewComment(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                            addComment();
                          }
                        }}
                        className='min-h-[60px]'
                      />
                      <Button
                        onClick={addComment}
                        disabled={!newComment.trim()}
                        className='flex-shrink-0'
                      >
                        <Send className='h-4 w-4' />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </PageFadeIn>
        </PageTransition>
    );

    if (isGlobalView) {
      return (
        <>
          <SharedNavbar isGlobalToolsOpen={globalToolsOpen} onGlobalToolsToggle={handleGlobalToolsToggle} />
          <GlobalToolsExpandingPanel isOpen={globalToolsOpen} onClose={() => setGlobalToolsOpen(false)} currentTool="posts" />
          {detailContent}
        </>
      );
    }

    return (
      <AppShell3 project={project}>
        {detailContent}
      </AppShell3>
    );
  }

  // Show posts list

  // Extract actions to reuse in both global and space views
  const actionsContent = (
            <>
              {/* Search Input */}
              <div className="relative w-64">
                <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search posts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-8 pl-8 pr-8 text-sm"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              {/* Space Filter Dropdown - For global view (multi-select) */}
              {isGlobalView && allAvailableSpaces && allAvailableSpaces.length > 0 && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-sm gap-1.5"
                    >
                      <Grid3X3 className="h-3.5 w-3.5" />
                      {selectedSpaceFilters.length > 0
                        ? `${selectedSpaceFilters.length} space${selectedSpaceFilters.length !== 1 ? 's' : ''}`
                        : "All Spaces"}
                      <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[300px] p-0" align="end">
                    <Command>
                      <CommandInput placeholder="Search spaces..." />
                      <CommandEmpty>No spaces found.</CommandEmpty>
                      <CommandGroup className="max-h-64 overflow-auto">
                        <CommandItem
                          onSelect={() => setSelectedSpaceFilters([])}
                        >
                          <Check
                            className={`mr-2 h-4 w-4 ${
                              selectedSpaceFilters.length === 0 ? "opacity-100" : "opacity-0"
                            }`}
                          />
                          All Spaces
                        </CommandItem>
                        {allAvailableSpaces.map(space => {
                          const isSelected = selectedSpaceFilters.includes(space.id);
                          return (
                            <CommandItem
                              key={space.id}
                              value={space.name}
                              onSelect={() => {
                                setSelectedSpaceFilters(prev =>
                                  prev.includes(space.id)
                                    ? prev.filter(id => id !== space.id)
                                    : [...prev, space.id]
                                );
                              }}
                            >
                              <Check
                                className={`mr-2 h-4 w-4 ${
                                  isSelected ? "opacity-100" : "opacity-0"
                                }`}
                              />
                              {space.name}
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}

              {/* Space Filter Dropdown - Only for stack view */}
              {isStackView && stackProjects && stackProjects.length > 0 && (
                <Select
                  value={selectedSpaceFilter}
                  onValueChange={setSelectedSpaceFilter}
                >
                  <SelectTrigger className='w-[200px] h-8 text-sm'>
                    <SelectValue placeholder='Filter by space' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='all'>
                      All Spaces ({stackProjects.length})
                    </SelectItem>
                    {stackProjects.map(project => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* Filters Popover */}
              <Popover open={filtersOpen} onOpenChange={(open) => {
                if (open) {
                  setTempTrackingFilters({ ...trackingFilters });
                }
                setFiltersOpen(open);
              }}>
                <PopoverTrigger asChild>
                  <Button
                    variant='outline'
                    size='sm'
                    className='h-8 text-sm gap-1.5'
                  >
                    <Filter className='h-3.5 w-3.5' />
                    Filters
                    {trackingFilters.statuses.length > 0 && (
                      <span className='ml-1 bg-primary text-primary-foreground text-[10px] rounded-full px-1.5 min-w-[18px] h-[18px] flex items-center justify-center font-bold'>
                        {trackingFilters.statuses.length}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className='w-80' align='end'>
                  <div className='space-y-4'>
                    <div className='flex items-center justify-between'>
                      <h4 className='font-semibold text-sm'>Tracking Status</h4>
                      <Button
                        variant='ghost'
                        size='sm'
                        className='h-7 text-xs'
                        onClick={() => setFiltersOpen(false)}
                      >
                        <X className='h-3.5 w-3.5' />
                      </Button>
                    </div>

                    <div className='space-y-3'>
                      <Label className='text-xs font-medium text-gray-700'>Show posts that:</Label>
                      <div className='space-y-2'>
                        <div className='flex items-center space-x-2'>
                          <Checkbox
                            id='filter-needs-reading'
                            checked={tempTrackingFilters.statuses.includes('needs_reading')}
                            onCheckedChange={() => {
                              setTempTrackingFilters(prev => ({
                                ...prev,
                                statuses: prev.statuses.includes('needs_reading')
                                  ? prev.statuses.filter(s => s !== 'needs_reading')
                                  : [...prev.statuses, 'needs_reading']
                              }));
                            }}
                          />
                          <label
                            htmlFor='filter-needs-reading'
                            className='text-sm font-normal cursor-pointer flex-1'
                          >
                            I need to read
                          </label>
                        </div>
                        <div className='flex items-center space-x-2'>
                          <Checkbox
                            id='filter-needs-understanding'
                            checked={tempTrackingFilters.statuses.includes('needs_understanding')}
                            onCheckedChange={() => {
                              setTempTrackingFilters(prev => ({
                                ...prev,
                                statuses: prev.statuses.includes('needs_understanding')
                                  ? prev.statuses.filter(s => s !== 'needs_understanding')
                                  : [...prev.statuses, 'needs_understanding']
                              }));
                            }}
                          />
                          <label
                            htmlFor='filter-needs-understanding'
                            className='text-sm font-normal cursor-pointer flex-1'
                          >
                            I need to confirm understanding
                          </label>
                        </div>
                        <div className='flex items-center space-x-2'>
                          <Checkbox
                            id='filter-completed-reading'
                            checked={tempTrackingFilters.statuses.includes('completed_reading')}
                            onCheckedChange={() => {
                              setTempTrackingFilters(prev => ({
                                ...prev,
                                statuses: prev.statuses.includes('completed_reading')
                                  ? prev.statuses.filter(s => s !== 'completed_reading')
                                  : [...prev.statuses, 'completed_reading']
                              }));
                            }}
                          />
                          <label
                            htmlFor='filter-completed-reading'
                            className='text-sm font-normal cursor-pointer flex-1'
                          >
                            I've read
                          </label>
                        </div>
                        <div className='flex items-center space-x-2'>
                          <Checkbox
                            id='filter-completed-understanding'
                            checked={tempTrackingFilters.statuses.includes('completed_understanding')}
                            onCheckedChange={() => {
                              setTempTrackingFilters(prev => ({
                                ...prev,
                                statuses: prev.statuses.includes('completed_understanding')
                                  ? prev.statuses.filter(s => s !== 'completed_understanding')
                                  : [...prev.statuses, 'completed_understanding']
                              }));
                            }}
                          />
                          <label
                            htmlFor='filter-completed-understanding'
                            className='text-sm font-normal cursor-pointer flex-1'
                          >
                            I've confirmed understanding
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className='flex items-center gap-2 pt-2 border-t'>
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={() => {
                          setTempTrackingFilters({ statuses: [] });
                          setTrackingFilters({ statuses: [] });
                          setFiltersOpen(false);
                        }}
                        className='flex-1 h-8 text-sm'
                      >
                        Clear
                      </Button>
                      <Button
                        size='sm'
                        onClick={() => {
                          setTrackingFilters(tempTrackingFilters);
                          setFiltersOpen(false);
                        }}
                        className='flex-1 h-8 text-sm'
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              {/* Clear Filters Button */}
              {trackingFilters.statuses.length > 0 && (
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => {
                    setTrackingFilters({ statuses: [] });
                    setTempTrackingFilters({ statuses: [] });
                  }}
                  className='h-8 text-sm text-gray-600 hover:text-gray-900'
                >
                  Clear all
                </Button>
              )}

              <Button
                variant='outline'
                size='sm'
                className='gap-2'
                onClick={() => {
                  if (isSingleSpaceView) {
                    navigate(`/space/${projectId}/message-board/create`);
                  } else if (isStackView) {
                    navigate(`/stack/${stackId}/message-board/create`);
                  } else {
                    navigate('/posts/create');
                  }
                }}
              >
                <Plus className='h-4 w-4' />
                New Post
              </Button>
            </>
  );


  const listContent = (
    <PageTransition>
      <ToolHeader
        spaceName={isGlobalView ? 'Company' : (isStackView ? stackName : space?.name || 'Space')}
        spaceImage={isGlobalView ? null : (isStackView ? stackImage : space?.logo_url)}
        toolName='Posts'
        currentTool='posts'
        spaceTools={isGlobalView ? globalTools : spaceTools}
        stackId={stackId}
        isStackView={isStackView}
        actions={actionsContent}
        isGlobalView={isGlobalView}
      />
      <PageFadeIn>
        <div className='max-w-4xl mx-auto px-6 py-6 space-y-6'>
            {/* Edit Dialog */}
            <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
              <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-hidden p-0">
                <div className="overflow-y-auto max-h-[90vh] p-6">
                <DialogHeader>
                  <DialogTitle>Edit Post</DialogTitle>
                  <DialogDescription>Update your post</DialogDescription>
                </DialogHeader>
                <div className='space-y-4 mt-4'>
                  {/* Visibility Selector */}
                  <div>
                    <label className='text-sm font-medium mb-2 block'>
                      Who can see this post?
                    </label>
                    <div className='flex gap-2 p-1 bg-muted rounded-lg'>
                      <button
                        type='button'
                        onClick={() => {
                          setVisibilityType('company');
                          setFormData({
                            ...formData,
                            selectedProjectId: null,
                            selectedProjectIds: []
                          });
                        }}
                        className={`flex-1 flex flex-col items-center justify-center gap-1 px-4 py-3 rounded-md transition-colors ${
                          visibilityType === 'company'
                            ? 'bg-background shadow-sm'
                            : 'hover:bg-background/50'
                        }`}
                      >
                        <Building2 className='h-5 w-5' />
                        <span className='text-xs font-medium'>All Spaces</span>
                      </button>
                      <button
                        type='button'
                        onClick={() => {
                          setVisibilityType('space');
                          // In single space view, auto-select current space
                          if (isSingleSpaceView && projectId) {
                            setFormData({
                              ...formData,
                              selectedProjectIds: [projectId],
                              selectedProjectId: projectId
                            });
                          }
                        }}
                        className={`flex-1 flex flex-col items-center justify-center gap-1 px-4 py-3 rounded-md transition-colors ${
                          visibilityType === 'space'
                            ? 'bg-background shadow-sm'
                            : 'hover:bg-background/50'
                        }`}
                      >
                        <Grid3X3 className='h-5 w-5' />
                        <span className='text-xs font-medium'>{isSingleSpaceView ? 'This Space' : 'Select Space(s)'}</span>
                      </button>
                    </div>
                  </div>

                  {/* Space Multi-Select Dropdown */}
                  {(isGlobalView || isStackView) && visibilityType === 'space' && (
                    <div>
                      <label className='text-sm font-medium mb-1 block'>
                        Select Space(s)
                      </label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            className="w-full justify-between"
                          >
                            {formData.selectedProjectIds && formData.selectedProjectIds.length > 0
                              ? `${formData.selectedProjectIds.length} space${formData.selectedProjectIds.length !== 1 ? 's' : ''} selected`
                              : "Select spaces..."}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Search spaces..." />
                            <CommandEmpty>No spaces found.</CommandEmpty>
                            <CommandGroup className="max-h-64 overflow-auto">
                              {(isStackView ? stackProjects : allAvailableSpaces).map(proj => {
                                const isSelected = (formData.selectedProjectIds || []).includes(proj.id);
                                return (
                                  <CommandItem
                                    key={proj.id}
                                    value={proj.name}
                                    onSelect={() => {
                                      const currentIds = formData.selectedProjectIds || [];
                                      const newIds = currentIds.includes(proj.id)
                                        ? currentIds.filter(id => id !== proj.id)
                                        : [...currentIds, proj.id];
                                      setFormData({
                                        ...formData,
                                        selectedProjectIds: newIds,
                                        selectedProjectId: newIds.length === 1 ? newIds[0] : null
                                      });
                                    }}
                                  >
                                    <Check
                                      className={`mr-2 h-4 w-4 ${
                                        isSelected ? "opacity-100" : "opacity-0"
                                      }`}
                                    />
                                    {proj.name}
                                  </CommandItem>
                                );
                              })}
                            </CommandGroup>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      {/* Show selected spaces as badges */}
                      {formData.selectedProjectIds && formData.selectedProjectIds.length > 0 && (
                        <div className='flex flex-wrap gap-2 mt-2'>
                          {formData.selectedProjectIds.map(projId => {
                            const proj = (isStackView ? stackProjects : allAvailableSpaces).find(p => p.id === projId);
                            return proj ? (
                              <Badge key={projId} variant="secondary" className="gap-1 pr-1">
                                <span>{proj.name}</span>
                                <button
                                  type="button"
                                  className="ml-1 hover:bg-destructive/20 rounded-sm p-0.5 transition-colors"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const newIds = formData.selectedProjectIds.filter(id => id !== projId);
                                    setFormData({
                                      ...formData,
                                      selectedProjectIds: newIds,
                                      selectedProjectId: newIds.length === 1 ? newIds[0] : null
                                    });
                                  }}
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </Badge>
                            ) : null;
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <label className='text-sm font-medium'>Title</label>
                    <Input
                      placeholder='Post title...'
                      value={formData.title}
                      onChange={e =>
                        setFormData({ ...formData, title: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className='text-sm font-medium mb-2 block'>Content</label>
                    <RichTextEditor
                      content={formData.content}
                      onChange={(html) =>
                        setFormData({ ...formData, content: html })
                      }
                      placeholder="What's on your mind?"
                    />
                  </div>

                  <div className='flex justify-end gap-2'>
                    <Button
                      variant='outline'
                      onClick={() => setIsEditDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button onClick={updatePost}>Update Post</Button>
                  </div>
                </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Posts list */}
            <div className='space-y-3'>
              <AnimatePresence mode='popLayout'>
                {filteredPosts
                  .filter(post => {
                    // If not in stack view or filter is 'all', show all posts
                    if (!isStackView || selectedSpaceFilter === 'all')
                      return true;

                    // When a specific space is selected in stack view, show:
                    // 1. Company-wide posts
                    if (!post.project_id && (!post.selected_project_ids || post.selected_project_ids.length === 0)) {
                      return true;
                    }

                    // 2. Posts specific to this space
                    if (post.project_id === selectedSpaceFilter) {
                      return true;
                    }

                    // 3. Stack posts that include this space
                    if (!post.project_id && post.selected_project_ids && post.selected_project_ids.length > 0) {
                      return post.selected_project_ids.includes(selectedSpaceFilter);
                    }

                    return false;
                  })
                  .filter(post => {
                    // Apply tracking filters
                    if (trackingFilters.statuses.length === 0) return true;

                    const matchesFilter = trackingFilters.statuses.some(status => {
                      switch (status) {
                        case 'needs_reading':
                          return post.requires_reading && !post.user_has_read;
                        case 'needs_understanding':
                          return post.requires_understanding && !post.user_has_understood;
                        case 'completed_reading':
                          return post.requires_reading && post.user_has_read;
                        case 'completed_understanding':
                          return post.requires_understanding && post.user_has_understood;
                        default:
                          return false;
                      }
                    });

                    return matchesFilter;
                  })
                  .map(post => (
                    <motion.div
                      key={post.id}
                      layoutId={post.id}
                      layout
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      data-post-id={post.id}
                    >
                      <Card
                        className='cursor-pointer hover:shadow-lg transition-shadow'
                        onClick={() => handleSelectPost(post)}
                      >
                        <CardHeader>
                          <div className='flex items-start justify-between gap-4'>
                            <div className='flex-1 min-w-0'>
                              <div className='flex items-center gap-2 mb-2 flex-wrap'>
                                {post.pinned && (
                                  <Badge variant='secondary' className='gap-1'>
                                    <Pin className='h-3 w-3' />
                                    Pinned
                                  </Badge>
                                )}
                                {post.post_type === 'link' && (
                                  <Badge variant='outline' className='gap-1'>
                                    <Link2 className='h-3 w-3' />
                                    Link
                                  </Badge>
                                )}
                                {post.post_type === 'image' && (
                                  <Badge variant='outline' className='gap-1'>
                                    <ImageIcon className='h-3 w-3' />
                                    Image
                                  </Badge>
                                )}
                                {post.post_type === 'video' && (
                                  <Badge variant='outline' className='gap-1'>
                                    <Video className='h-3 w-3' />
                                    Video
                                  </Badge>
                                )}
                                {post.post_type === 'document' && (
                                  <Badge variant='outline' className='gap-1'>
                                    <FileUp className='h-3 w-3' />
                                    Document
                                  </Badge>
                                )}
                                {post.post_type === 'policy' && (
                                  <Badge variant='outline' className='gap-1 bg-violet-50 text-violet-700 border-violet-200'>
                                    <FileText className='h-3 w-3' />
                                    Policy
                                  </Badge>
                                )}
                                {/* Visibility Badges */}
                                {!post.project_id && (!post.selected_project_ids || post.selected_project_ids.length === 0) && (
                                  <Badge variant='outline' className='gap-1'>
                                    <Building2 className='h-3 w-3' />
                                    All Spaces
                                  </Badge>
                                )}
                                {post.project_id && (
                                  <Badge variant='outline' className='gap-1'>
                                    <Grid3X3 className='h-3 w-3' />
                                    {post.projects?.name || 'Space'}
                                  </Badge>
                                )}
                                {!post.project_id && post.selected_project_ids && post.selected_project_ids.length > 0 && (
                                  <MultiSpaceBadge selectedProjectIds={post.selected_project_ids} />
                                )}
                                {/* Tracking Requirement Badges */}
                                {post.requires_reading && (
                                  <Badge
                                    variant={post.user_has_read ? 'secondary' : 'destructive'}
                                    className={`gap-1 ${post.user_has_read ? 'bg-green-100 text-green-700 hover:bg-green-100' : ''}`}
                                  >
                                    <ClipboardCheck className='h-3 w-3' />
                                    {post.user_has_read ? '✓ Read' : 'Needs Reading'}
                                  </Badge>
                                )}
                                {post.requires_understanding && (
                                  <Badge
                                    variant={post.user_has_understood ? 'secondary' : 'destructive'}
                                    className={`gap-1 ${post.user_has_understood ? 'bg-green-100 text-green-700 hover:bg-green-100' : ''}`}
                                  >
                                    <CheckSquare className='h-3 w-3' />
                                    {post.user_has_understood ? '✓ Understood' : 'Needs Understanding'}
                                  </Badge>
                                )}
                              </div>
                              <CardTitle className='mb-2'>
                                {post.title}
                              </CardTitle>
                              <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                                <Avatar className='h-5 w-5'>
                                  <AvatarImage src={post.author?.avatar_url} />
                                  <AvatarFallback>
                                    <User className='h-3 w-3' />
                                  </AvatarFallback>
                                </Avatar>
                                <span>
                                  {post.author?.display_name ||
                                    `${post.author?.first_name || ''} ${
                                      post.author?.last_name || ''
                                    }`.trim() ||
                                    'Unknown'}
                                </span>
                                <span>•</span>
                                <span>
                                  {formatDistanceToNow(
                                    new Date(post.created_date),
                                    { addSuffix: true }
                                  )}
                                </span>
                                <span>•</span>
                                <span className='flex items-center gap-1'>
                                  <MessageSquare className='h-3 w-3' />
                                  {post.comments?.[0]?.count || 0}
                                </span>
                              </div>
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger
                                asChild
                                onClick={e => e.stopPropagation()}
                              >
                                <Button
                                  variant='ghost'
                                  size='sm'
                                  className='h-8 w-8 p-0'
                                >
                                  <MoreVertical className='h-4 w-4' />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align='end'>
                                <DropdownMenuItem
                                  onClick={e => {
                                    e.stopPropagation();
                                    togglePin(post);
                                  }}
                                >
                                  <Pin className='h-4 w-4 mr-2' />
                                  {post.pinned ? 'Unpin' : 'Pin'} Post
                                </DropdownMenuItem>
                                {userProfile &&
                                  post.created_by === userProfile.id && (
                                    <>
                                      <DropdownMenuItem
                                        onClick={e => {
                                          e.stopPropagation();
                                          startEditPost(post);
                                        }}
                                      >
                                        <Edit className='h-4 w-4 mr-2' />
                                        Edit
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        onClick={e => {
                                          e.stopPropagation();
                                          deletePost(post.id);
                                        }}
                                        className='text-red-600'
                                      >
                                        <Trash2 className='h-4 w-4 mr-2' />
                                        Delete
                                      </DropdownMenuItem>
                                    </>
                                  )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                          {/* Post Preview based on type */}
                          {post.post_type === 'text' && post.content && (
                            <div className='mt-2 line-clamp-2'>
                              <TipTapContent
                                content={post.content}
                                className='text-sm text-muted-foreground'
                              />
                            </div>
                          )}

                          {post.post_type === 'link' && (
                            <div className='mt-2 flex items-center gap-2 text-sm text-muted-foreground'>
                              <ExternalLink className='h-3 w-3' />
                              <span className='truncate'>{post.link_url}</span>
                            </div>
                          )}

                          {post.post_type === 'image' && (
                            <div className='mt-2 flex items-center gap-2 text-sm text-muted-foreground'>
                              <ImageIcon className='h-3 w-3' />
                              <span>Image post</span>
                            </div>
                          )}

                          {post.post_type === 'video' && (
                            <div className='mt-2 flex items-center gap-2 text-sm text-muted-foreground'>
                              <Video className='h-3 w-3' />
                              <span>Video post</span>
                            </div>
                          )}

                          {post.post_type === 'document' && (
                            <div className='mt-2 flex items-center gap-2 text-sm text-muted-foreground'>
                              <FileUp className='h-3 w-3' />
                              <span className='truncate'>{post.document_name}</span>
                            </div>
                          )}

                          {post.post_type === 'policy' && (
                            <div className='mt-2 flex items-center gap-2 text-sm text-violet-600'>
                              <FileText className='h-3 w-3' />
                              <span className='truncate'>{post.document_name || 'Policy document'}</span>
                              {post.document_size && (
                                <Badge variant='outline' className='text-xs'>
                                  {formatFileSize(post.document_size)}
                                </Badge>
                              )}
                            </div>
                          )}
                        </CardHeader>
                      </Card>
                    </motion.div>
                  ))}
              </AnimatePresence>

              {filteredPosts.filter(post => {
                if (!isStackView || selectedSpaceFilter === 'all') return true;
                return post.project_id === selectedSpaceFilter;
              }).length === 0 && (
                <Card>
                  <CardContent className='flex flex-col items-center justify-center py-12'>
                    <MessageSquare className='h-16 w-16 text-muted-foreground opacity-50 mb-4' />
                    <h3 className='text-lg font-semibold mb-2'>
                      {isStackView && selectedSpaceFilter !== 'all'
                        ? 'No posts in this space'
                        : 'No posts yet'}
                    </h3>
                    <p className='text-muted-foreground mb-4'>
                      {isStackView && selectedSpaceFilter !== 'all'
                        ? 'Try selecting a different space or create a new post'
                        : 'Be the first to start a discussion'}
                    </p>
                    <Button
                      onClick={() => setIsCreateDialogOpen(true)}
                      className='gap-2'
                    >
                      <Plus className='h-4 w-4' />
                      Create First Post
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </PageFadeIn>

        {/* Quiz Modal - Simple Custom Modal */}
        {console.log('Should render modal?', isQuizModalOpen, !!selectedPost, !!selectedPost?.quiz_data)}
        {isQuizModalOpen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.8)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px'
            }}
            onClick={() => setIsQuizModalOpen(false)}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                maxWidth: '800px',
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto',
                padding: '24px'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className='p-6'>
                <div className='mb-6'>
                  <div className='flex items-center gap-2 text-xl font-semibold mb-2'>
                    <GraduationCap className='h-6 w-6' />
                    {selectedPost.quiz_data.quizTitle || 'Quiz'}
                  </div>
                  <p className='text-gray-600 text-sm'>
                    {selectedPost.quiz_data.quizDescription}
                  </p>
                </div>

              {!quizSubmitted ? (
                <div className='space-y-6 py-4'>
                  {selectedPost?.quiz_data?.questions?.map((question, index) => (
                    <div key={index} className='space-y-3 p-4 border rounded-lg bg-muted/20'>
                      <div className='font-medium text-sm'>
                        Question {index + 1} of {selectedPost.quiz_data.questions.length}
                      </div>
                      <div className='font-semibold'>{question.question}</div>

                      {question.type === 'multiple_choice' && (
                        <div className='space-y-2'>
                          {question.options?.map((option, optIndex) => (
                            <div key={optIndex} className='flex items-center space-x-2'>
                              <input
                                type='radio'
                                id={`q${index}-opt${optIndex}`}
                                name={`question-${index}`}
                                value={option}
                                checked={quizAnswers[index] === option}
                                onChange={() => handleQuizAnswer(index, option)}
                                className='w-4 h-4'
                              />
                              <label
                                htmlFor={`q${index}-opt${optIndex}`}
                                className='cursor-pointer text-sm'
                              >
                                {option}
                              </label>
                            </div>
                          ))}
                        </div>
                      )}

                      {question.type === 'true_false' && (
                        <div className='space-y-2'>
                          {['True', 'False'].map((option) => (
                            <div key={option} className='flex items-center space-x-2'>
                              <input
                                type='radio'
                                id={`q${index}-${option}`}
                                name={`question-${index}`}
                                value={option}
                                checked={quizAnswers[index] === option}
                                onChange={() => handleQuizAnswer(index, option)}
                                className='w-4 h-4'
                              />
                              <label
                                htmlFor={`q${index}-${option}`}
                                className='cursor-pointer text-sm'
                              >
                                {option}
                              </label>
                            </div>
                          ))}
                        </div>
                      )}

                      {question.type === 'short_answer' && (
                        <Input
                          placeholder='Type your answer...'
                          value={quizAnswers[index] || ''}
                          onChange={(e) => handleQuizAnswer(index, e.target.value)}
                        />
                      )}
                    </div>
                  ))}

                  <div className='flex justify-end gap-2 pt-4'>
                    <Button
                      variant='outline'
                      onClick={() => setIsQuizModalOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSubmitQuiz}
                      disabled={
                        Object.keys(quizAnswers).length !==
                        selectedPost?.quiz_data?.questions?.length
                      }
                      className='bg-blue-600 hover:bg-blue-700'
                    >
                      Submit Quiz
                    </Button>
                  </div>
                </div>
              ) : (
                <div className='space-y-6 py-4'>
                  <div className='text-center space-y-4'>
                    <div
                      className={`text-6xl font-bold ${
                        quizScore >= 70 ? 'text-green-600' : 'text-orange-600'
                      }`}
                    >
                      {quizScore}%
                    </div>
                    <div className='text-lg font-semibold'>
                      {quizScore >= 70
                        ? 'Congratulations! You passed!'
                        : 'Keep trying!'}
                    </div>
                    <p className='text-sm text-muted-foreground'>
                      You got{' '}
                      {Math.round(
                        (quizScore / 100) * selectedPost.quiz_data.questions.length
                      )}{' '}
                      out of {selectedPost.quiz_data.questions.length} questions correct
                    </p>
                  </div>

                  <div className='space-y-3'>
                    {selectedPost?.quiz_data?.questions?.map((question, index) => {
                      const userAnswer = quizAnswers[index];
                      const isCorrect =
                        question.type === 'short_answer'
                          ? userAnswer?.toLowerCase().trim() ===
                            question.correct_answer?.toLowerCase().trim()
                          : userAnswer === question.correct_answer;

                      return (
                        <div
                          key={index}
                          className={`p-4 border rounded-lg ${
                            isCorrect
                              ? 'bg-green-50 border-green-200'
                              : 'bg-red-50 border-red-200'
                          }`}
                        >
                          <div className='flex items-start gap-2'>
                            {isCorrect ? (
                              <Check className='h-5 w-5 text-green-600 mt-0.5' />
                            ) : (
                              <X className='h-5 w-5 text-red-600 mt-0.5' />
                            )}
                            <div className='flex-1 space-y-2'>
                              <div className='font-medium'>{question.question}</div>
                              <div className='text-sm'>
                                <span className='font-medium'>Your answer: </span>
                                <span
                                  className={
                                    isCorrect ? 'text-green-700' : 'text-red-700'
                                  }
                                >
                                  {userAnswer || 'No answer'}
                                </span>
                              </div>
                              {!isCorrect && (
                                <div className='text-sm'>
                                  <span className='font-medium'>Correct answer: </span>
                                  <span className='text-green-700'>
                                    {question.correct_answer}
                                  </span>
                                </div>
                              )}
                              {question.explanation && (
                                <div className='text-sm text-muted-foreground mt-2 pt-2 border-t'>
                                  <span className='font-medium'>Explanation: </span>
                                  {question.explanation}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className='flex justify-end gap-2 pt-4'>
                    <Button
                      variant='outline'
                      onClick={() => setIsQuizModalOpen(false)}
                    >
                      Close
                    </Button>
                    <Button onClick={handleRetakeQuiz} className='bg-blue-600 hover:bg-blue-700'>
                      Retake Quiz
                    </Button>
                  </div>
                </div>
              )}
              </div>
            </div>
          </div>
        )}
      </PageTransition>
  );

  // Quiz Modal - Rendered at root level for reactivity
  const quizModal = isQuizModalOpen && (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.8)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
      onClick={() => setIsQuizModalOpen(false)}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          maxWidth: '800px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          padding: '24px'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {selectedPost?.quiz_data ? (
          <>
            <div className='mb-6'>
              <div className='flex items-center gap-2 text-xl font-semibold mb-2'>
                <GraduationCap className='h-6 w-6' />
                {selectedPost.quiz_data.quizTitle || 'Quiz'}
              </div>
              <p className='text-gray-600 text-sm'>
                {selectedPost.quiz_data.quizDescription}
              </p>
            </div>
            <div>
              <h2 className='text-2xl font-bold mb-4'>QUIZ MODAL IS WORKING!</h2>
              <Button onClick={() => setIsQuizModalOpen(false)}>Close</Button>
            </div>
          </>
        ) : (
          <div>No quiz data</div>
        )}
      </div>
    </div>
  );

  if (isGlobalView) {
    return (
      <>
        <SharedNavbar isGlobalToolsOpen={globalToolsOpen} onGlobalToolsToggle={handleGlobalToolsToggle} />
        <GlobalToolsExpandingPanel isOpen={globalToolsOpen} onClose={() => setGlobalToolsOpen(false)} currentTool="posts" />
        {listContent}
        {quizModal}
      </>
    );
  }

  return (
    <AppShell3 project={project}>
      {listContent}
      {quizModal}
    </AppShell3>
  );
}
